import { Navigate, Outlet, createFileRoute, useLocation } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'
import { getReportsPathAccess } from '@/features/reports/nav/reports-nav-items'
import { ReportsShellSkeleton } from '@/features/reports/nav/reports-shell-skeleton'
import { ReportsWorkspaceLayout } from '@/features/reports/nav/reports-workspace-layout'
import { useReportsViewer } from '@/features/reports/nav/use-reports-viewer'
import { reportsSearchMiddlewares, validateReportsSearch } from '@/features/reports/period/reports-period-search'
import { formatDocumentTitle } from '@/lib/document-title'

/**
 * Reports area layout: the sub-menu frame around every Reports page, and the `?period=` every page shares (kept
 * across sub-page links by the search middleware). Nothing protected mounts until access resolves; a typed URL of an
 * unknown page or one the salon's setup has no place for goes to the first tab (like Settings), and one the viewer's
 * grant doesn't cover shows the access message inside the frame.
 */
export const Route = createFileRoute('/_auth/$orgId/reports')({
  validateSearch: validateReportsSearch,
  search: { middlewares: reportsSearchMiddlewares },
  component: ReportsLayout,
})

function ReportsLayout() {
  const { orgId } = Route.useParams()
  const viewer = useReportsViewer()
  const pathname = useLocation({ select: (location) => location.pathname })

  if (viewer.isLoading) return <ReportsShellSkeleton />
  const access = getReportsPathAccess(viewer, pathname)
  if (access.kind === 'redirect') return <Navigate to={access.to} params={{ orgId }} replace />

  return (
    <>
      {/* Area title for the access messages; each page sets its own over it. */}
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Reports')}</title>
      </Helmet>
      {viewer.canReport ? (
        <ReportsWorkspaceLayout orgId={orgId}>
          {access.kind === 'open' ? <Outlet key={viewer.revision} /> : <div className="p-6 text-[16px]">{access.message}</div>}
        </ReportsWorkspaceLayout>
      ) : (
        <ReportsAccessRequired />
      )}
    </>
  )
}

function ReportsAccessRequired() {
  return <div className="p-6 text-[16px]">Reports access is required. Ask your salon admin.</div>
}
